import { isDefined } from '@fabric-app/utils/guards';
import { useState, useEffect } from 'react';
import equal from '../node_modules/fast-deep-equal/index.js';
import debounce from '../node_modules/lodash-es/debounce.js';

function isRefObject(ref) {
  return ref !== null && "current" in ref;
}
function isHTMLElement(ref) {
  return ref !== null && ref instanceof HTMLElement;
}
const overflowScrollableValues = [
  "scroll",
  "auto"
];
const useDomContentRect = (ref, options) => {
  const [htmlElRect, setHtmlElRect] = useState(null);
  const { includeRectPosition = false, adjustByScrollOffset = 0, onRectChange, debounceDelay = 0, observeParentResize = false, observeAttributes = false, observeWindowResize = false, keepRectOnNullRef = false, calculationKey } = options ?? {};
  useEffect(() => {
    if (!ref) {
      return;
    }
    const refEl = isRefObject(ref) ? ref.current : isHTMLElement(ref) ? ref : null;
    if (refEl) {
      const updateRect = debounce(() => {
        const rect = refEl.getBoundingClientRect();
        const scrollOffset = adjustByScrollOffset;
        let offsetX = 0;
        let offsetY = 0;
        if (scrollOffset > 0) {
          let parent = refEl.parentElement;
          let depth = 0;
          while (parent && parent !== document.body && depth < scrollOffset) {
            const computedStyle = window.getComputedStyle(parent);
            if (overflowScrollableValues.includes(computedStyle.overflowY) || overflowScrollableValues.includes(computedStyle.overflowX)) {
              offsetX += parent.scrollLeft;
              offsetY += parent.scrollTop;
              depth++;
            }
            parent = parent.parentElement;
          }
        }
        const nextState = includeRectPosition ? {
          // define each property otherwise it assumes it as equal wrongly
          bottom: rect.bottom + offsetY,
          left: rect.left + offsetX,
          right: rect.right + offsetX,
          top: rect.top + offsetY,
          x: rect.left + offsetX,
          y: rect.top + offsetY,
          height: rect.height,
          width: rect.width,
          toJSON: rect.toJSON
        } : {
          width: rect.width,
          height: rect.height
        };
        setHtmlElRect((prevState) => {
          if (equal(prevState, nextState)) {
            return prevState;
          } else {
            onRectChange?.(nextState);
            return nextState;
          }
        });
      }, debounceDelay);
      updateRect();
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (isDefined(entry) && isDefined(refEl)) {
          updateRect();
        }
      });
      resizeObserver.observe(refEl);
      resizeObserver.observe(document.body);
      if (observeParentResize && refEl.parentElement) {
        resizeObserver.observe(refEl.parentElement);
      }
      let mutationObserver = null;
      if (observeAttributes) {
        mutationObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === "attributes") {
              updateRect();
            }
          }
        });
        mutationObserver.observe(refEl, {
          attributes: true
        });
      }
      if (includeRectPosition) window.addEventListener("scroll", updateRect);
      if (observeWindowResize) window.addEventListener("resize", updateRect);
      return () => {
        if (!keepRectOnNullRef) setHtmlElRect(null);
        if (mutationObserver) mutationObserver.disconnect();
        resizeObserver.disconnect();
        if (includeRectPosition) window.removeEventListener("scroll", updateRect);
        if (observeWindowResize) window.removeEventListener("resize", updateRect);
      };
    } else {
      if (!keepRectOnNullRef) setHtmlElRect(null);
    }
  }, [
    ref,
    observeParentResize,
    debounceDelay,
    observeAttributes,
    observeWindowResize,
    keepRectOnNullRef,
    includeRectPosition,
    onRectChange,
    calculationKey,
    adjustByScrollOffset
  ]);
  return [
    htmlElRect,
    setHtmlElRect
  ];
};

export { useDomContentRect };
//# sourceMappingURL=useDomContentRect.js.map
